import * as React from "react";
import { useBlocker } from "@tanstack/react-router";

// Shared "browser back closes overlays instead of navigating away" helper.
//
// While `open` is true, the hook uses TanStack Router's blocker to intercept
// any back / forward / swipe-back navigation attempt. On block it closes the
// overlay and cancels the navigation, so the user stays on the same page and
// only the dialog / sheet / drilled-in view is dismissed.

// Global counter of open overlays. Other blockers (e.g. the "back always goes
// home" interceptor in the authenticated layout) can check this to skip
// intercepting when an overlay is already handling the back gesture.
let openOverlayCount = 0;
export function hasOpenOverlay() {
  return openOverlayCount > 0;
}

export function useHistoryBackClose(open: boolean, onClose: () => void) {
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    if (!open) return;
    openOverlayCount += 1;
    return () => {
      openOverlayCount = Math.max(0, openOverlayCount - 1);
    };
  }, [open]);

  const { status, reset } = useBlocker({
    shouldBlockFn: () => open,
    withResolver: true,
    enableBeforeUnload: false,
  });

  React.useEffect(() => {
    if (status === "blocked") {
      onCloseRef.current();
      reset();
    }
  }, [status, reset]);
}
